// Data-access layer: React Query hooks over the Due Health v1 API.
//
// Every page reads server state through these hooks (never axios directly), so
// caching, loading/error state, and invalidation live in one place. Mutations
// invalidate the query keys they affect, keeping the UI consistent.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, patch, post, put } from '../lib/apiClient';

// Stable query-key factory.
export const qk = {
  reference: ['reference'],
  worklist: (scope, opts) => ['worklist', scope, opts],
  worklistSummary: (scope, priorAuth) => ['worklist', 'summary', scope, priorAuth],
  appointmentExecution: (id, scope) => ['appointment', 'execution', id, scope],
  workflows: (scope, opts) => ['workflows', scope, opts],
  queueCounts: (scope, priorAuth) => ['workflows', 'counts', scope, priorAuth],
  workflow: (id, scope) => ['workflows', id, scope],
  eligibility: (scope) => ['eligibility', scope],
  eligibilityDetail: (id) => ['eligibility', id],
  discrepancies: (scope, patientId) => ['discrepancies', scope, patientId],
  networkQueue: (scope) => ['network', 'queue', scope],
  networkPlans: (scope) => ['network', 'plans', scope],
  planProfiles: (scope) => ['network', 'profiles', scope],
  networkVerifications: (scope) => ['network', 'verifications', scope],
  networkThreshold: (scope) => ['network', 'threshold', scope],
  planLearnings: (scope, planKey) => ['network', 'learnings', scope, planKey || 'all'],
  claims: (scope) => ['claims', scope],
  claimsStats: (scope) => ['claims', 'stats', scope],
  denialPatterns: (scope) => ['denial-patterns', scope],
  denialDigests: ['denial-digests'],
  contracts: (scope) => ['contracts', scope],
  contract: (id) => ['contracts', id],
  feeSchedule: (scope) => ['fee-schedule', scope],
  finance: (scope, priorAuth) => ['finance', scope, priorAuth],
  stedi: (scope) => ['analytics', 'stedi', scope],
  trends: (scope, weeks) => ['analytics', 'trends', scope, weeks],
  practices: ['practices'],
  practice: (id) => ['practices', id],
  providers: (scope) => ['providers', scope],
  payers: ['payers'],
  plans: ['plans'],
  patients: (scope, q) => ['patients', scope, q],
  patient: (id) => ['patients', id],
  locations: (scope) => ['locations', scope],
  contractedPlans: (scope) => ['contracted-plans', scope],
  policies: (scope) => ['policies', scope],
  automationRules: (scope) => ['automation-rules', scope],
  agents: (scope) => ['agents', scope],
  agentEvents: (scope) => ['agents', 'events', scope],
  team: (scope) => ['team', scope],
};

const enabled = (scope) => ({ enabled: scope != null });

// --------------------------------- system ---------------------------------
export const useReference = () =>
  useQuery({ queryKey: qk.reference, queryFn: () => get('/reference'), staleTime: Infinity });

// -------------------------------- worklist ---------------------------------
export const useWorklist = (scope, { timeframe, priorAuth } = {}) =>
  useQuery({
    queryKey: qk.worklist(scope, { timeframe, priorAuth }),
    queryFn: () => get('/worklist', { practiceId: scope, timeframe, priorAuth }),
    ...enabled(scope),
  });

export const useWorklistSummary = (scope, priorAuth = false) =>
  useQuery({
    queryKey: qk.worklistSummary(scope, priorAuth),
    queryFn: () => get('/worklist/summary', { practiceId: scope, priorAuth }),
    ...enabled(scope),
  });

// Per-appointment execution view: pipeline stages, agent chain, profile, cost.
// Pass the active practice scope so the request stays within the caller's tenant.
export const useAppointmentExecution = (appointmentId, scope) =>
  useQuery({
    queryKey: qk.appointmentExecution(appointmentId, scope),
    queryFn: () => get(`/appointments/${appointmentId}/execution`, { practiceId: scope }),
    enabled: appointmentId != null && scope != null,
  });

// -------------------------------- workflows --------------------------------
export const useWorkflows = (scope, { queue, priorAuth } = {}) =>
  useQuery({
    queryKey: qk.workflows(scope, { queue, priorAuth }),
    queryFn: () => get('/workflows', { practiceId: scope, queue, priorAuth }),
    ...enabled(scope),
  });

export const useQueueCounts = (scope, priorAuth = false) =>
  useQuery({
    queryKey: qk.queueCounts(scope, priorAuth),
    queryFn: () => get('/workflows/queues/counts', { practiceId: scope, priorAuth }),
    ...enabled(scope),
  });

export const useWorkflow = (id, scope) =>
  useQuery({
    queryKey: qk.workflow(id, scope),
    queryFn: () => get(`/workflows/${id}`, { practiceId: scope }),
    enabled: !!id && scope != null,
  });

export const useWorkflowAction = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action, note }) => post(`/workflows/${id}/action`, { action, note }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflows'] });
      qc.invalidateQueries({ queryKey: ['worklist'] });
      qc.invalidateQueries({ queryKey: ['network'] });
      qc.invalidateQueries({ queryKey: ['discrepancies'] });
      qc.invalidateQueries({ queryKey: ['appointment'] });
    },
  });
};

// ------------------------------- eligibility -------------------------------
export const useEligibility = (scope) =>
  useQuery({
    queryKey: qk.eligibility(scope),
    queryFn: () => get('/eligibility', { practiceId: scope }),
    ...enabled(scope),
  });

export const useEligibilityDetail = (id) =>
  useQuery({
    queryKey: qk.eligibilityDetail(id),
    queryFn: () => get(`/eligibility/${id}`),
    enabled: !!id,
  });

export const useRunEligibility = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ patientId, appointmentId, providerId, memberId, serviceTypeCodes }) =>
      post('/eligibility/run', { patientId, appointmentId, providerId, memberId, serviceTypeCodes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['worklist'] });
      qc.invalidateQueries({ queryKey: ['eligibility'] });
    },
  });
};

// ------------------------------ discrepancies ------------------------------
export const useDiscrepancies = (scope, patientId) =>
  useQuery({
    queryKey: qk.discrepancies(scope, patientId),
    queryFn: () => get('/discrepancies', { practiceId: scope, patientId }),
    ...enabled(scope),
  });

export const useDiscrepancyAction = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }) => post(`/discrepancies/${id}/action`, { action }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['discrepancies'] }),
  });
};

// --------------------------------- network ---------------------------------
export const useNetworkQueue = (scope) =>
  useQuery({
    queryKey: qk.networkQueue(scope),
    queryFn: () => get('/network/queue', { practiceId: scope }),
    ...enabled(scope),
  });

export const useNetworkPlans = (scope) =>
  useQuery({
    queryKey: qk.networkPlans(scope),
    queryFn: () => get('/network/plans', { practiceId: scope }),
    ...enabled(scope),
  });

// Per-plan rollups: cases dealt, denial history, trust stage, and plan risk.
// The authoritative plan universe (added + contracted + observed-on-271).
export const useNetworkPlanProfiles = (scope) =>
  useQuery({
    queryKey: qk.planProfiles(scope),
    queryFn: () => get('/network/plans/profiles', { practiceId: scope }),
    ...enabled(scope),
  });

export const useNetworkVerifications = (scope) =>
  useQuery({
    queryKey: qk.networkVerifications(scope),
    queryFn: () => get('/network/verifications', { practiceId: scope }),
    ...enabled(scope),
  });

export const useNetworkThreshold = (scope) =>
  useQuery({
    queryKey: qk.networkThreshold(scope),
    queryFn: () => get('/network/threshold', { practiceId: scope }),
    ...enabled(scope),
  });

const invalidateNetwork = (qc) => {
  qc.invalidateQueries({ queryKey: ['network'] });
  qc.invalidateQueries({ queryKey: ['worklist'] });
  qc.invalidateQueries({ queryKey: ['eligibility'] });
};

export const useAddNetworkPlan = () => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (body) => post('/network/plans', body), onSuccess: () => invalidateNetwork(qc) });
};

export const useAddVerification = () => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (body) => post('/network/verifications', body), onSuccess: () => invalidateNetwork(qc) });
};

export const useSetThreshold = () => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (body) => put('/network/threshold', body), onSuccess: () => invalidateNetwork(qc) });
};

// Admin-only: mark a plan trusted (or revoke). A trusted plan bypasses the
// verification gate and is treated as "established". Body: { practiceId,
// payerId, planName, planKey, trusted }.
export const useSetPlanTrusted = () => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (body) => post('/network/plans/trust', body), onSuccess: () => invalidateNetwork(qc) });
};

export const fetchConfidence = (params) => get('/network/confidence', params);

// Appointments affected by a plan's network status — every appointment whose
// latest 271 resolves to this planKey, re-scored against the current network
// library. Fetched (not cached) before and after a status change to diff the
// per-appointment risk impact.
export const fetchPlanAppointments = (params) => get('/network/plans/appointments', params);

// Per-plan "what we've learned" notes. Pass planKey to scope to one plan
// (appointment detail), or omit for the whole practice (worklist chips).
export const usePlanLearnings = (scope, { planKey } = {}) =>
  useQuery({
    queryKey: qk.planLearnings(scope, planKey),
    queryFn: () => get('/network/learnings', { practiceId: scope, ...(planKey ? { planKey } : {}) }),
    ...enabled(scope),
  });

// ---------------------------------- claims ---------------------------------
export const useClaims = (scope) =>
  useQuery({ queryKey: qk.claims(scope), queryFn: () => get('/claims', { practiceId: scope }), ...enabled(scope) });

export const useClaimsStats = (scope) =>
  useQuery({ queryKey: qk.claimsStats(scope), queryFn: () => get('/claims/stats', { practiceId: scope }), ...enabled(scope) });

export const useDenialPatterns = (scope) =>
  useQuery({ queryKey: qk.denialPatterns(scope), queryFn: () => get('/denial-patterns', { practiceId: scope }), ...enabled(scope) });

export const useDenialDigests = () =>
  useQuery({ queryKey: qk.denialDigests, queryFn: () => get('/denial-digests') });

export const useContracts = (scope) =>
  useQuery({ queryKey: qk.contracts(scope), queryFn: () => get('/contracts', { practiceId: scope }), ...enabled(scope) });

export const useContract = (id) =>
  useQuery({ queryKey: qk.contract(id), queryFn: () => get(`/contracts/${id}`), enabled: !!id });

// Contracted fee-schedule lines (cpt → contracted rate) for the estimator.
export const useFeeSchedule = (scope) =>
  useQuery({ queryKey: qk.feeSchedule(scope), queryFn: () => get('/fee-schedule', { practiceId: scope }), ...enabled(scope) });

// ---------------------------------- finance --------------------------------
export const useFinanceOverview = (scope, priorAuth = false) =>
  useQuery({
    queryKey: qk.finance(scope, priorAuth),
    queryFn: () => get('/finance/overview', { practiceId: scope, priorAuth }),
    ...enabled(scope),
  });

// --------------------------------- analytics -------------------------------
export const useStediAnalytics = (scope) =>
  useQuery({ queryKey: qk.stedi(scope), queryFn: () => get('/analytics/stedi', { practiceId: scope }), ...enabled(scope) });

export const useStediTrends = (scope, weeks = 12) =>
  useQuery({ queryKey: qk.trends(scope, weeks), queryFn: () => get('/analytics/trends', { practiceId: scope, weeks }), ...enabled(scope) });

// One-shot analytics question → chart spec (used by the copilot).
export const runAnalyticsQuery = (query, practiceId) =>
  post('/analytics/query', { query, practiceId });

// --------------------------------- directory -------------------------------
export const usePractices = () =>
  useQuery({ queryKey: qk.practices, queryFn: () => get('/practices') });

export const useProviders = (scope) =>
  useQuery({ queryKey: qk.providers(scope), queryFn: () => get('/providers', { practiceId: scope }), ...enabled(scope) });

export const useAddProvider = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => post('/providers', body),
    onSuccess: (provider) => {
      // Insert into the cached list synchronously so the new provider is an
      // available option *before* a caller auto-selects it (Radix Select won't
      // display a value whose item mounted after the value was set). Then refetch.
      qc.setQueryData(qk.providers(provider.practiceId), (old) =>
        Array.isArray(old) ? [...old, provider] : old);
      qc.invalidateQueries({ queryKey: ['providers'] });
    },
  });
};

export const usePayers = () => useQuery({ queryKey: qk.payers, queryFn: () => get('/payers') });
export const usePlans = () => useQuery({ queryKey: qk.plans, queryFn: () => get('/plans') });

export const usePatients = (scope, q) =>
  useQuery({ queryKey: qk.patients(scope, q), queryFn: () => get('/patients', { practiceId: scope, q }), ...enabled(scope) });

export const usePatient = (id) =>
  useQuery({ queryKey: qk.patient(id), queryFn: () => get(`/patients/${id}`), enabled: !!id });

export const useLocations = (scope) =>
  useQuery({ queryKey: qk.locations(scope), queryFn: () => get('/locations', { practiceId: scope }), ...enabled(scope) });

// Practice team roster (Settings → Users & roles).
export const useTeam = (scope) =>
  useQuery({ queryKey: qk.team(scope), queryFn: () => get('/team', { practiceId: scope }), ...enabled(scope) });

export const useContractedPlans = (scope) =>
  useQuery({ queryKey: qk.contractedPlans(scope), queryFn: () => get('/contracted-plans', { practiceId: scope }), ...enabled(scope) });

export const useCreateAppointment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => post('/appointments', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['worklist'] });
      qc.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
};

// -------------------------------- governance -------------------------------
export const usePolicies = (scope) =>
  useQuery({ queryKey: qk.policies(scope), queryFn: () => get('/policies', { practiceId: scope }), ...enabled(scope) });

export const useUpdatePolicy = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) => patch(`/policies/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['policies'] }),
  });
};

export const useAutomationRules = (scope) =>
  useQuery({ queryKey: qk.automationRules(scope), queryFn: () => get('/automation-rules', { practiceId: scope }), ...enabled(scope) });

export const useAgents = (scope) =>
  useQuery({ queryKey: qk.agents(scope), queryFn: () => get('/agents', { practiceId: scope }), ...enabled(scope) });

export const useAgentEvents = (scope) =>
  useQuery({ queryKey: qk.agentEvents(scope), queryFn: () => get('/agents/events', { practiceId: scope }), ...enabled(scope) });

// ---------------------------------- copilot --------------------------------
export const askCopilot = (message, practiceId) => post('/copilot', { message }, { practiceId });
